// PolicyEditor — structured editor for the policy content shape.
//
// MVP6 expansion: the brace block was a single `exec.rules` list,
// now it's three sibling subsystems (syscalls / process_guard /
// sensitive_file_access). The editor uses a tabbed sub-layout to
// keep the cognitive load manageable as the schema grows.
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import Button from "@/components/Button";
import ChipEditor from "@/components/ChipEditor";
import type { components } from "@/lib/api/gen/schema";

type PolicyContent = components["schemas"]["PolicyContent"];
type PolicyMcpServer = components["schemas"]["PolicyMcpServer"];
type PolicyBrace = components["schemas"]["PolicyBrace"];
type PolicyBraceSyscalls = components["schemas"]["PolicyBraceSyscalls"];
type PolicyProcessGuard = components["schemas"]["PolicyProcessGuard"];
type PolicySensitiveFileAccess = components["schemas"]["PolicySensitiveFileAccess"];
type PolicyTargetProcessSelector = components["schemas"]["PolicyTargetProcessSelector"];
type PolicyBraceFilterRegex = components["schemas"]["PolicyBraceFilterRegex"];
type PolicyBraceNetwork = components["schemas"]["PolicyBraceNetwork"];
type PolicyBraceAllowToEntry = components["schemas"]["PolicyBraceAllowToEntry"];

// ── Warden (LLM egress) types ────────────────────────────────────────
// The Go policy model (api/internal/policy/model.go) accepts a `warden`
// block alongside mcp/brace; a per-sandbox MITM proxy inspects outbound
// LLM API calls and pins which model IDs a sandbox may reach. The
// OpenAPI generator (`npm run gen`) hasn't picked the block up yet, so
// the types live here locally. Serialized into `content.warden.model_pin`
// on save; the extra key survives openapi-fetch's JSON.stringify body.
type PolicyWardenModelPin = {
  enable: boolean;
  remediate?: boolean;
  deny_list?: string[];
  allow_list?: string[];
};
type PolicyWarden = {
  model_pin?: PolicyWardenModelPin;
};
// Widening view over PolicyContent that also carries the warden block,
// so we can read/write it without any-casts scattered through the file.
type ContentWithWarden = PolicyContent & { warden?: PolicyWarden };

export interface PolicyDraft {
  name: string;
  description: string;
  content: PolicyContent;
}

interface Props {
  initial: PolicyDraft;
  /** Disables the `name` field. Set when editing an existing policy
   *  (name is immutable per OpenAPI PolicyUpdate). */
  lockName?: boolean;
  onSave: (draft: PolicyDraft) => void | Promise<void>;
  onCancel: () => void;
  saving?: boolean;
  saveError?: string | null;
}

// Empty starting content the Create flow initializes with.
// Both mcp and brace blocks omitted — at least one is required at
// save time, so the UI enables "Add server" / "Add brace section"
// to bring them in.
export const emptyContent: PolicyContent = {
  mcp: { servers: {} },
};

// Pre-baked BlueRock recommended brace baseline. Mirrors MVP6's
// shipping defaults: pragmatic allowlist of system bin paths, the
// nc/wget/curl deny, the 35-path sensitive-file set, and the system-
// auth program exceptions. Loaded via "Load BlueRock baseline"
// button so customers don't start from scratch.
const RECOMMENDED_BRACE_BASELINE: PolicyBrace = {
  process_guard: {
    enable: true,
    remediate: true,
    inline: true,
    allow: [
      "/bin/**",
      "/sbin/**",
      "/usr/bin/**",
      "/usr/sbin/**",
      "/usr/local/bin/**",
      "/usr/local/sbin/**",
      "/usr/lib*/**",
      "/opt/**",
      "/etc/**",
      "/lib*/**",
    ],
    deny: ["**/nc", "**/wget", "**/curl"],
    deny_exceptions: [],
  },
  sensitive_file_access: {
    enable: true,
    remediate: false,
    inline: true,
    paths: [
      { path: "/etc/shadow", write_only: false },
      { path: "/etc/sudoers", write_only: false },
      { path: "/etc/sudoers.d/*", write_only: false },
      { path: "/etc/ssh/*", write_only: false },
      { path: "/etc/passwd", write_only: true },
      { path: "/etc/group", write_only: true },
      { path: "/etc/hostname", write_only: true },
      { path: "/etc/hosts", write_only: true },
      { path: "/etc/resolv.conf", write_only: true },
      { path: "**/.ssh/id_rsa", write_only: false },
      { path: "**/.ssh/id_ed25519", write_only: false },
      { path: "**/.ssh/authorized_keys", write_only: false },
      { path: "**/.aws/credentials", write_only: false },
      { path: "**/.netrc", write_only: false },
      { path: "**/.env", write_only: false },
      { path: "**/.env.*", write_only: false },
      { path: "**/.git/credentials", write_only: false },
      { path: "**/.claude/.credentials.json", write_only: false },
      { path: "**/.codex/*", write_only: false },
      { path: "**/.gemini/*", write_only: false },
      { path: "/proc/*/environ", write_only: false },
      { path: "/proc/*/maps", write_only: false },
    ],
    exceptions: [
      // System auth / login machinery — sshd, PAM helpers, login, su,
      // sudo. Without these every shell login fires a violation.
      { program: "/usr/sbin/sshd", file: "**/*" },
      { program: "/usr/sbin/unix_chkpwd", file: "**/*" },
      { program: "/bin/login", file: "**/*" },
      { program: "/bin/su", file: "**/*" },
      { program: "/usr/bin/sudo", file: "**/*" },
      // Common ops CLIs that legitimately read credential dotfiles.
      // aws reads ~/.aws/credentials; kubectl reads ~/.kube/config and
      // friends. The first-class agent (claude/codex/etc.) is
      // intentionally NOT in this list — that's the threat model.
      { program: "**/aws", file: "**/*" },
      { program: "**/kubectl", file: "**/*" },
    ],
  },
  syscalls: {
    enable: true,
    suspicious_syscalls: {
      enable: true,
      remediate: true,
      inline: true,
      allow_unshare: false,
      allow_ptrace: false,
    },
    mmap_exec: { enable: true, remediate: false, inline: true },
  },
};

// Server name + tool name regex mirror the API's policy/schema.go
// validator. Catching client-side is a UX win — the user sees the
// error immediately rather than after a round-trip.
// Mirror of api/internal/policy/uc.ProcessGuardBaselineAllow. Read-only,
// shown above the editable allow list so the operator sees the survival
// paths the translator silently merges in. Duplication is acknowledged
// — the Go side is canonical (merge happens server-side regardless of
// what the UI shows); this list is purely visual. Keep them in sync.
const PROCESS_GUARD_BASELINE_ALLOW = [
  "/bin/**",
  "/usr/bin/**",
  "/usr/sbin/**",
  "/usr/local/bin/**",
  "/usr/lib/**",
  "/lib*/**",
];

const SERVER_NAME_RE = /^[a-zA-Z0-9_.-]{1,64}$/;
const TOOL_NAME_RE = /^[a-zA-Z0-9_.-]{1,128}$/;

const TRANSPORTS = ["stdio", "sse", "http"] as const;
type Transport = (typeof TRANSPORTS)[number];

export default function PolicyEditor({
  initial,
  lockName,
  onSave,
  onCancel,
  saving,
  saveError,
}: Props) {
  const [draft, setDraft] = useState<PolicyDraft>(initial);
  const [newServer, setNewServer] = useState("");
  const [newServerErr, setNewServerErr] = useState<string | null>(null);

  const servers = draft.content.mcp?.servers ?? {};
  const brace = draft.content.brace;

  type TransportField = "default_stdio_policy" | "default_sse_policy" | "default_http_policy";
  const transportPolicy = (field: TransportField): "allow" | "deny" =>
    draft.content.mcp?.[field] === "deny" ? "deny" : "allow";

  const setServers = (next: Record<string, PolicyMcpServer>) => {
    setDraft({
      ...draft,
      content: {
        ...draft.content,
        mcp: { ...(draft.content.mcp ?? {}), servers: next },
      },
    });
  };
  const setTransportPolicy = (field: TransportField, next: "allow" | "deny") => {
    setDraft({
      ...draft,
      content: {
        ...draft.content,
        mcp: {
          ...(draft.content.mcp ?? { servers: {} }),
          [field]: next,
        },
      },
    });
  };
  // MCP enforcement mode: block (remediate=true, the default) vs alert only.
  const mcpBlock = (): boolean => draft.content.mcp?.remediate !== false;
  const setMcpBlock = (block: boolean) => {
    setDraft({
      ...draft,
      content: {
        ...draft.content,
        mcp: { ...(draft.content.mcp ?? { servers: {} }), remediate: block },
      },
    });
  };
  const setBrace = (next: PolicyBrace | undefined) => {
    setDraft({
      ...draft,
      content: { ...draft.content, brace: next },
    });
  };
  const patchBrace = (patch: Partial<PolicyBrace>) => {
    setBrace({ ...(brace ?? {}), ...patch });
  };

  // Warden (LLM egress). Lives at content.warden.model_pin. undefined =
  // section untouched → omitted from the save payload entirely (mirrors
  // how brace is conditionally included).
  const warden = (draft.content as ContentWithWarden).warden;
  const setWarden = (next: PolicyWarden | undefined) => {
    const content = { ...draft.content } as ContentWithWarden;
    if (next === undefined) delete content.warden;
    else content.warden = next;
    setDraft({ ...draft, content });
  };

  const addServer = () => {
    const name = newServer.trim();
    if (!name) return;
    if (!SERVER_NAME_RE.test(name)) {
      setNewServerErr("alnum, dash, underscore, dot; 1–64 chars");
      return;
    }
    if (servers[name]) {
      setNewServerErr(`"${name}" already added`);
      return;
    }
    setServers({ ...servers, [name]: { allow_list: [], deny_list: [] } });
    setNewServer("");
    setNewServerErr(null);
  };

  const removeServer = (name: string) => {
    const next = { ...servers };
    delete next[name];
    setServers(next);
  };

  const updateServer = (name: string, patch: Partial<PolicyMcpServer>) => {
    setServers({ ...servers, [name]: { ...servers[name], ...patch } });
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        // Strip empty mcp block — validator requires the servers map
        // to be non-empty WHEN mcp is set. emptyContent ships with
        // `mcp: {servers: {}}` for the create flow's "Add server"
        // affordance, but on submit we want to drop it if the user
        // never actually added any servers (and didn't flip stdio
        // policy).
        const out: PolicyDraft = { ...draft, content: { ...draft.content } };
        const m = out.content.mcp;
        if (m && Object.keys(m.servers ?? {}).length === 0 && !m.default_stdio_policy) {
          out.content = { ...out.content, mcp: undefined };
        }
        onSave(out);
      }}
    >
      <section className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Name</span>
          <input
            required
            disabled={lockName || saving}
            pattern="^.{1,100}$"
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm disabled:opacity-60"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          {lockName && (
            <span className="mt-1 block text-xs text-text-muted">
              Name is set at create time and cannot be changed.
            </span>
          )}
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Description</span>
          <input
            disabled={saving}
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </label>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">MCP servers</h3>
          <div className="text-xs text-text-muted">
            Per-server tool allow + deny lists. Set <strong>command</strong>{" "}
            (full launch path) if a brace exec rule should match this server.
          </div>
        </div>

        <div className="rounded border border-border bg-surface/40 p-3">
          <div className="mb-2 flex items-baseline justify-between">
            <div className="text-sm font-medium">Default transport policy</div>
            <div className="text-xs text-text-muted">
              per-transport disposition for servers not listed above
            </div>
          </div>
          <div className="text-xs text-text-muted">
            <strong>allow</strong>: any MCP server on that transport may
            launch/connect. <strong>deny</strong>: only servers listed
            below — with a matching <em>command</em> (stdio) or{" "}
            <em>URL</em> (sse/http) — are permitted; everything else is
            blocked at the enforcement layer. Already-running servers are
            unaffected; only new launches/connections.
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            {([
              ["default_stdio_policy", "stdio"],
              ["default_sse_policy", "sse"],
              ["default_http_policy", "http"],
            ] as const).map(([field, label]) => (
              <TransportPolicyToggle
                key={field}
                label={label}
                value={transportPolicy(field)}
                onChange={(v) => setTransportPolicy(field, v)}
              />
            ))}
          </div>
        </div>

        <div className="rounded border border-border bg-surface/40 p-3">
          <div className="mb-2 flex items-baseline justify-between">
            <div className="text-sm font-medium">Enforcement mode</div>
            <div className="text-xs text-text-muted">
              what happens when a denied MCP call is detected
            </div>
          </div>
          <div className="text-xs text-text-muted">
            <strong>Block</strong>: deny the tool call at the enforcement layer.{" "}
            <strong>Alert</strong>: log the violation but let the call through
            (observe-only). Applies to every MCP denial in this policy.
          </div>
          <div className="mt-3">
            <McpModeToggle
              value={mcpBlock() ? "block" : "alert"}
              onChange={(v) => setMcpBlock(v === "block")}
            />
          </div>
        </div>
        {Object.entries(servers).map(([name, srv]) => (
          <div
            key={name}
            className="rounded border border-border bg-surface/40 p-3"
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="font-mono text-sm">{name}</span>
              <button
                type="button"
                onClick={() => removeServer(name)}
                className="text-text-muted hover:text-danger"
                aria-label={`Remove server ${name}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block md:col-span-2">
                <span className="mb-1 block text-xs uppercase text-text-muted">
                  command
                </span>
                <input
                  className="w-full rounded border border-border bg-surface px-2 py-1 font-mono text-xs"
                  placeholder="fastmcp run /home/dev/mcp-servers/myserver/main.py"
                  value={srv.command ?? ""}
                  onChange={(e) => updateServer(name, { command: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs uppercase text-text-muted">
                  transport
                </span>
                <select
                  className="rounded border border-border bg-surface px-2 py-1 text-xs"
                  value={srv.transport ?? ""}
                  onChange={(e) =>
                    updateServer(name, {
                      transport: (e.target.value || undefined) as Transport | undefined,
                    })
                  }
                >
                  <option value="">(unset)</option>
                  {TRANSPORTS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <div className="md:col-span-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs uppercase text-text-muted">
                    allow_list
                  </div>
                  <ChipEditor
                    compact
                    values={srv.allow_list ?? []}
                    onChange={(next) => updateServer(name, { allow_list: next })}
                    placeholder="tool name"
                    validate={(t) =>
                      TOOL_NAME_RE.test(t) ? null : "alnum, dash, underscore, dot; 1–128 chars"
                    }
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs uppercase text-text-muted">
                    deny_list
                  </div>
                  <ChipEditor
                    compact
                    values={srv.deny_list ?? []}
                    onChange={(next) => updateServer(name, { deny_list: next })}
                    placeholder="tool name"
                    validate={(t) =>
                      TOOL_NAME_RE.test(t) ? null : "alnum, dash, underscore, dot; 1–128 chars"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            className="flex-1 rounded border border-border bg-surface px-2 py-1.5 text-sm"
            placeholder="MCP server name (e.g. github)"
            value={newServer}
            onChange={(e) => {
              setNewServer(e.target.value);
              setNewServerErr(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addServer();
              }
            }}
          />
          <button
            type="button"
            onClick={addServer}
            disabled={!newServer.trim()}
            className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-elev disabled:opacity-40"
          >
            <Plus size={12} /> Add server
          </button>
        </div>
        {newServerErr && <div className="text-xs text-danger">{newServerErr}</div>}
      </section>

      <BraceSection brace={brace} setBrace={setBrace} patchBrace={patchBrace} />

      <WardenSection warden={warden} setWarden={setWarden} />


      {saveError && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {saveError}
        </div>
      )}

      <div className="flex justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

// ─── Brace section: tabs for Process Guard / Sensitive Files / Syscalls ───
//
// Empty-state shows two CTAs: "Load BlueRock baseline" (populates with
// the recommended config) and "Start empty" (initializes minimal
// process_guard so the rest of the editor has something to render).

function BraceSection({
  brace,
  setBrace,
  patchBrace,
}: {
  brace: PolicyBrace | undefined;
  setBrace: (b: PolicyBrace | undefined) => void;
  patchBrace: (patch: Partial<PolicyBrace>) => void;
}) {
  const [tab, setTab] = useState<
    "process_guard" | "sensitive_files" | "syscalls" | "filter_regex" | "network"
  >("process_guard");

  if (
    !brace ||
    (!brace.process_guard &&
      !brace.sensitive_file_access &&
      !brace.syscalls &&
      !brace.filter_regex &&
      !brace.network)
  ) {
    return (
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Brace</h3>
          <span className="text-xs text-text-muted">
            In-sandbox runtime enforcement: process, file-access, and syscall controls
          </span>
        </div>
        <div className="rounded border border-dashed border-border p-4 text-sm text-text-secondary">
          No brace policy yet. Start from BlueRock's recommended baseline
          (a sensible allow/deny + sensitive paths set) or build from
          scratch.
          <div className="mt-3 flex gap-2">
            <Button type="button" variant="primary" onClick={() => setBrace(RECOMMENDED_BRACE_BASELINE)}>
              Load BlueRock baseline
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                setBrace({
                  process_guard: { enable: false, remediate: false, inline: true, allow: [], deny: [] },
                })
              }
            >
              Start empty
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Brace</h3>
        <button
          type="button"
          onClick={() => setBrace(undefined)}
          className="text-xs text-text-muted hover:text-danger"
        >
          Remove runtime section
        </button>
      </div>

      <div role="tablist" className="flex flex-wrap gap-1 border-b border-border text-xs">
        {(
          [
            ["process_guard", "Process Guard", brace.process_guard?.enable],
            ["sensitive_files", "Sensitive Files", brace.sensitive_file_access?.enable],
            ["syscalls", "Syscalls", brace.syscalls?.enable],
            ["filter_regex", "MCP Tool Filter", brace.filter_regex?.enable],
            ["network", "Network", brace.network?.enable],
          ] as const
        ).map(([id, label, enabled]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={
              "border-b-2 px-3 py-1.5 " +
              (tab === id
                ? "border-accent text-text-primary"
                : "border-transparent text-text-secondary hover:text-text-primary")
            }
          >
            {label}
            {enabled !== undefined && (
              <span
                className={
                  "ml-2 inline-block h-1.5 w-1.5 rounded-full " +
                  (enabled ? "bg-success" : "bg-text-muted/40")
                }
                aria-label={enabled ? "enabled" : "disabled"}
              />
            )}
          </button>
        ))}
      </div>

      {tab === "process_guard" && (
        <ProcessGuardEditor
          value={brace.process_guard}
          onChange={(next) => patchBrace({ process_guard: next })}
        />
      )}
      {tab === "sensitive_files" && (
        <SensitiveFileAccessEditor
          value={brace.sensitive_file_access}
          onChange={(next) => patchBrace({ sensitive_file_access: next })}
        />
      )}
      {tab === "syscalls" && (
        <SyscallsEditor value={brace.syscalls} onChange={(next) => patchBrace({ syscalls: next })} />
      )}
      {tab === "filter_regex" && (
        <FilterRegexEditor
          value={brace.filter_regex}
          onChange={(next) => patchBrace({ filter_regex: next })}
        />
      )}
      {tab === "network" && (
        <NetworkEditor
          value={brace.network}
          onChange={(next) => patchBrace({ network: next })}
        />
      )}
    </section>
  );
}

// ─── Warden — LLM Egress ─────────────────────────────────────────────
// Pins which LLM models a governed sandbox may reach. A per-sandbox MITM
// proxy inspects outbound LLM API calls and returns 403 "blocked by
// warden policy" for a model whose ID matches deny_list; when allow_list
// is non-empty only matching models are permitted (deny takes
// precedence). Serialized to content.warden.model_pin; the section is
// omitted entirely from the save payload until the operator adds it,
// mirroring the brace conditional-include discipline.
//
// Model-id globs are validated against the server-side pattern
// [a-zA-Z0-9_.:/*-]{1,128}; the ChipEditor rejects anything else so the
// operator sees the error immediately instead of after a round-trip.
const WARDEN_GLOB_RE = /^[a-zA-Z0-9_.:/*-]{1,128}$/;

function WardenSection({
  warden,
  setWarden,
}: {
  warden: PolicyWarden | undefined;
  setWarden: (next: PolicyWarden | undefined) => void;
}) {
  const pin = warden?.model_pin;

  if (!pin) {
    return (
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Warden — LLM Egress</h3>
          <span className="text-xs text-text-muted">
            Pin which LLM models a sandbox agent may reach
          </span>
        </div>
        <div className="rounded border border-dashed border-border p-4 text-sm text-text-secondary">
          Block sandbox agents from reaching disallowed LLM models. A
          per-sandbox proxy inspects outbound LLM API calls and denies any
          model matching the deny list.
          <div className="mt-3">
            <Button
              type="button"
              variant="primary"
              onClick={() =>
                setWarden({
                  model_pin: {
                    enable: true,
                    remediate: true,
                    deny_list: [],
                    allow_list: [],
                  },
                })
              }
            >
              Add model-pin policy
            </Button>
          </div>
        </div>
      </section>
    );
  }

  const patchPin = (patch: Partial<PolicyWardenModelPin>) =>
    setWarden({ model_pin: { ...pin, ...patch } });

  const validateGlob = (token: string): string | null =>
    WARDEN_GLOB_RE.test(token)
      ? null
      : "model globs only: letters, digits, and _ . : / * -  (1–128 chars)";

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Warden — LLM Egress</h3>
        <button
          type="button"
          onClick={() => setWarden(undefined)}
          className="text-xs text-text-muted hover:text-danger"
        >
          Remove LLM-egress section
        </button>
      </div>

      <div className="rounded border border-border bg-surface/30 p-3 text-xs text-text-muted">
        A per-sandbox proxy inspects outbound LLM API calls and returns
        <span className="mx-1 font-mono">403 blocked by warden policy</span>
        for a model whose ID matches the deny list. Patterns are model-id
        globs, e.g. <code className="font-mono">gpt-*</code>,{" "}
        <code className="font-mono">o1-*</code>,{" "}
        <code className="font-mono">claude-*</code>.{" "}
        <strong>Deny takes precedence.</strong> When the allow list is
        non-empty, only matching models are permitted.
      </div>

      <div className="rounded border border-border bg-surface/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!pin.enable}
              onChange={(e) => patchPin({ enable: e.target.checked })}
            />
            <span>
              <span className="font-medium">Enable model pinning</span>
              <span className="ml-1 text-xs text-text-muted">
                — gate the outbound LLM-model check
              </span>
            </span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase text-text-muted">mode</span>
            <McpModeToggle
              value={pin.remediate === false ? "alert" : "block"}
              onChange={(v) => patchPin({ remediate: v === "block" })}
            />
          </div>
        </div>
        <div className="mt-1.5 text-xs text-text-muted">
          <strong>Block</strong> denies disallowed models at the proxy;{" "}
          <strong>Alert</strong> observes only (logs the call, lets it
          through).
        </div>
      </div>

      <div className="rounded border border-border bg-surface/40 p-3">
        <div className="mb-1 flex items-baseline justify-between">
          <div className="text-xs uppercase text-text-muted">deny list</div>
          <div className="text-xs text-text-muted">
            model globs blocked — always evaluated
          </div>
        </div>
        <ChipEditor
          values={pin.deny_list ?? []}
          onChange={(next) => patchPin({ deny_list: next })}
          placeholder="gpt-*"
          validate={validateGlob}
        />
      </div>

      <div className="rounded border border-border bg-surface/40 p-3">
        <div className="mb-1 flex items-baseline justify-between">
          <div className="text-xs uppercase text-text-muted">allow list</div>
          <div className="text-xs text-text-muted">
            optional — when set, only matching models are permitted
          </div>
        </div>
        <ChipEditor
          values={pin.allow_list ?? []}
          onChange={(next) => patchPin({ allow_list: next })}
          placeholder="claude-*"
          validate={validateGlob}
        />
      </div>
    </section>
  );
}

// ── MCP Tool Filter editor ──────────────────────────────────────
// Regex patterns evaluated against MCP tool traffic. Enforced at two
// layers: (a) bluepython at MCP message boundaries for local stdio
// MCPs (insurance-mcp etc.), and (b) bluerock-mcp-pretool.py Claude
// Code hook for remote/hosted MCPs that bluepython doesn't see
// (e.g. Anthropic-hosted Microsoft 365 connector). Patterns are
// synced from the deployed bru_policy to each workspace's hook
// config by /usr/local/bin/bluerock-mcp-patterns-sync after every
// bluerock-deploy. Maps to agentic_sensor.mcp.{tool_arg_filter,
// tool_description_filter,tool_response_filter}.forbidden_*_patterns
// on the deployed bru_policy. Most demos only populate
// tool_arg_patterns (e.g. `(volumeDelete|projectDelete)\s*\(` for
// Railway). Patterns are Rust regex::Regex (RE2 subset).
function FilterRegexEditor({
  value,
  onChange,
}: {
  value: PolicyBraceFilterRegex | undefined;
  onChange: (next: PolicyBraceFilterRegex) => void;
}) {
  const f: PolicyBraceFilterRegex =
    value ?? { enable: true, remediate: true, tool_arg_patterns: [] };

  return (
    <div className="space-y-3">
      <ModeToggle
        label="mode"
        mode={modeOf(f.enable, f.remediate)}
        onChange={(m) => onChange({ ...f, ...patchForMode(m) })}
      />
      <div className="rounded border border-border bg-surface/30 p-3 text-xs text-text-muted">
        Regular-expression patterns matched against MCP tool traffic.
        Enforced for both local and remote/hosted MCP servers. Invalid
        patterns are rejected at save time with a line-numbered error.
        Examples:
        <code className="ml-1 font-mono">(volumeDelete|projectDelete)\s*\(</code>
        {" "}or <code className="font-mono">bluerocksecurityio\.sharepoint\.com/sites/insurance-restricted</code>.
      </div>
      <PatternList
        label="tool arg patterns"
        hint="matched against tools/call argument payloads"
        values={f.tool_arg_patterns ?? []}
        onChange={(next) => onChange({ ...f, tool_arg_patterns: next })}
      />
      <PatternList
        label="tool description patterns"
        hint="matched against tool descriptions in tools/list responses"
        values={f.tool_description_patterns ?? []}
        onChange={(next) => onChange({ ...f, tool_description_patterns: next })}
      />
      <PatternList
        label="tool response patterns"
        hint="matched against tools/call response payloads"
        values={f.tool_response_patterns ?? []}
        onChange={(next) => onChange({ ...f, tool_response_patterns: next })}
      />
    </div>
  );
}

// PatternList — one-pattern-per-line editor with mono font + remove
// button. Validates each entry as a JS regex on-the-fly (Go side
// re-validates as Go regexp — both are RE2-shaped so a pattern that
// passes the client check almost always passes the server check too).
function PatternList({
  label,
  hint,
  values,
  onChange,
}: {
  label: string;
  hint: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const validate = (s: string): string | null => {
    if (!s.trim()) return "empty";
    try {
      new RegExp(s);
      return null;
    } catch (e) {
      return (e as Error).message;
    }
  };
  return (
    <div className="rounded border border-border bg-surface/40 p-3">
      <div className="mb-1 flex items-baseline justify-between">
        <div className="text-xs uppercase text-text-muted">{label}</div>
        <div className="text-xs text-text-muted">{hint}</div>
      </div>
      <div className="space-y-1.5">
        {values.map((p, i) => {
          const err = validate(p);
          return (
            <div key={i} className="flex items-center gap-2">
              <input
                className={
                  "flex-1 rounded border bg-surface px-2 py-1 font-mono text-xs " +
                  (err ? "border-danger/60" : "border-border")
                }
                value={p}
                onChange={(e) => {
                  const next = [...values];
                  next[i] = e.target.value;
                  onChange(next);
                }}
              />
              <button
                type="button"
                onClick={() => onChange(values.filter((_, j) => j !== i))}
                className="text-text-muted hover:text-danger"
                aria-label="Remove pattern"
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => onChange([...values, ""])}
          className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-elev"
        >
          <Plus size={12} /> Add pattern
        </button>
      </div>
    </div>
  );
}

// ── BRace Network editor ────────────────────────────────────────────
// Per-sandbox L3/L4 egress allowlist. Maps to
// brace.network.firewall.egress.allow_to[] on the deployed bru_policy.
// Default-deny model: only listed (addr × ports × proto) triples are
// permitted from sandboxes attached to bru-net. addr accepts CIDR
// (1.1.1.1/32, 10.0.0.0/16) OR hostname (resolved at deploy time).
function NetworkEditor({
  value,
  onChange,
}: {
  value: PolicyBraceNetwork | undefined;
  onChange: (next: PolicyBraceNetwork) => void;
}) {
  const n: PolicyBraceNetwork = value ?? { enable: false, allow_to: [] };
  const entries = n.allow_to ?? [];

  const setEntry = (i: number, patch: Partial<PolicyBraceAllowToEntry>) => {
    const next = [...entries];
    next[i] = { ...next[i], ...patch };
    onChange({ ...n, allow_to: next });
  };

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 rounded border border-border bg-surface/40 p-3 text-sm">
        <input
          type="checkbox"
          checked={!!n.enable}
          onChange={(e) => onChange({ ...n, enable: e.target.checked })}
        />
        <span>
          <span className="font-medium">Enable per-sandbox firewall</span>
          <span className="ml-1 text-xs text-text-muted">
            — default-deny egress; only entries below are reachable
          </span>
        </span>
      </label>

      <div className="rounded border border-border bg-surface/30 p-3 text-xs text-text-muted">
        <code>addr</code> accepts CIDR (<code>1.1.1.1/32</code>,{" "}
        <code>10.0.0.0/16</code>) or hostname (resolved at deploy time, e.g. {" "}
        <code>backboard.railway.app</code>). For DNS, use{" "}
        <code>0.0.0.0/0</code> with port 53 + proto <code>udp</code>.
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-[2fr_1fr_1fr_auto] items-center gap-2 px-2 text-xs uppercase text-text-muted">
          <span>addr (CIDR or hostname)</span>
          <span>ports (comma)</span>
          <span>proto</span>
          <span />
        </div>
        {entries.map((e, i) => (
          <div
            key={i}
            className="grid grid-cols-[2fr_1fr_1fr_auto] items-center gap-2 rounded border border-border bg-surface p-2"
          >
            <input
              className="rounded border border-border bg-surface px-2 py-1 font-mono text-xs"
              placeholder="1.1.1.1/32 or backboard.railway.app"
              value={e.addr}
              onChange={(ev) => setEntry(i, { addr: ev.target.value })}
            />
            <input
              className="rounded border border-border bg-surface px-2 py-1 font-mono text-xs"
              placeholder="80,443"
              value={(e.ports ?? []).join(",")}
              onChange={(ev) =>
                setEntry(i, {
                  ports: ev.target.value
                    .split(",")
                    .map((s) => parseInt(s.trim(), 10))
                    .filter((n) => Number.isFinite(n) && n >= 0 && n <= 65535),
                })
              }
            />
            <select
              className="rounded border border-border bg-surface px-2 py-1 text-xs"
              value={(e.proto ?? ["tcp"])[0]}
              onChange={(ev) =>
                setEntry(i, { proto: [ev.target.value as "tcp" | "udp"] })
              }
            >
              <option value="tcp">tcp</option>
              <option value="udp">udp</option>
            </select>
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...n,
                  allow_to: entries.filter((_, j) => j !== i),
                })
              }
              className="text-text-muted hover:text-danger"
              aria-label="Remove entry"
              title="Remove"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            onChange({
              ...n,
              allow_to: [
                ...entries,
                { addr: "", ports: [443], proto: ["tcp"] },
              ],
            })
          }
          className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-elev"
        >
          <Plus size={12} /> Add allow rule
        </button>
      </div>
    </div>
  );
}

// Mode = the user-facing collapse of (enable, remediate, inline) into
// a single Off / Alert / Block segmented control. Maps as:
//   Off   → enable=false                    (and remediate/inline irrelevant)
//   Alert → enable=true,  remediate=false   (events fire, action allowed)
//   Block → enable=true,  remediate=true, inline=true  (action denied synchronously)
// Async-block (remediate=true,inline=false) is a rare ops choice; if anyone
// needs it we'll add a "Block (async)" option later. The Off/Alert/Block
// segmented control matches what operators saw in the PaaS UI.
type Mode = "off" | "alert" | "block";

function modeOf(enable?: boolean, remediate?: boolean): Mode {
  if (!enable) return "off";
  if (remediate) return "block";
  return "alert";
}

function patchForMode(m: Mode): { enable: boolean; remediate?: boolean; inline?: boolean } {
  if (m === "off") return { enable: false, remediate: false };
  if (m === "alert") return { enable: true, remediate: false };
  return { enable: true, remediate: true, inline: true };
}

// ModeToggle — three-state Off / Alert / Block segmented control.
// Yellow for alert, red for block, muted for off — matches PaaS UI.
function ModeToggle({
  mode,
  onChange,
  allowBlock = true,
  label,
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
  allowBlock?: boolean; // hide Block for observation-only rules
  label?: string;
}) {
  const options: Mode[] = allowBlock ? ["off", "alert", "block"] : ["off", "alert"];
  const className = (opt: Mode) => {
    if (mode !== opt) {
      return "bg-surface px-3 py-1 text-text-secondary hover:text-text-primary";
    }
    if (opt === "alert") return "bg-warning px-3 py-1 font-medium text-black";
    if (opt === "block") return "bg-danger px-3 py-1 font-medium text-white";
    return "bg-border/60 px-3 py-1 font-medium text-text-primary";
  };
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-xs uppercase text-text-muted">{label}</span>}
      <div
        role="tablist"
        aria-label={label ?? "mode"}
        className="inline-flex shrink-0 overflow-hidden rounded border border-border text-xs"
      >
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            role="tab"
            aria-selected={mode === opt}
            onClick={() => onChange(opt)}
            className={className(opt)}
          >
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

// TransportPolicyToggle — compact green/red two-button control with a
// label on the left. Used once per MCP transport (stdio / sse / http).
function TransportPolicyToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: "allow" | "deny";
  onChange: (v: "allow" | "deny") => void;
}) {
  const cls = (opt: "allow" | "deny") => {
    if (value !== opt) return "bg-surface px-3 py-1 text-text-secondary hover:text-text-primary";
    return opt === "deny"
      ? "bg-danger px-3 py-1 font-medium text-white"
      : "bg-success px-3 py-1 font-medium text-black";
  };
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-xs uppercase text-text-secondary">{label}</span>
      <div
        role="tablist"
        aria-label={`${label} policy`}
        className="inline-flex shrink-0 overflow-hidden rounded border border-border text-xs"
      >
        {(["allow", "deny"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            role="tab"
            aria-selected={value === opt}
            onClick={() => onChange(opt)}
            className={cls(opt)}
          >
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

// McpModeToggle — Alert / Block segmented control for MCP enforcement mode.
// Alert = observe (remediate=false); Block = enforce (remediate=true). MCP has
// no "off" state here — enforcement is always active; only the disposition changes.
function McpModeToggle({
  value,
  onChange,
}: {
  value: "alert" | "block";
  onChange: (v: "alert" | "block") => void;
}) {
  const cls = (opt: "alert" | "block") => {
    if (value !== opt) return "bg-surface px-3 py-1 text-text-secondary hover:text-text-primary";
    return opt === "block"
      ? "bg-danger px-3 py-1 font-medium text-white"
      : "bg-warning px-3 py-1 font-medium text-black";
  };
  return (
    <div
      role="tablist"
      aria-label="MCP enforcement mode"
      className="inline-flex shrink-0 overflow-hidden rounded border border-border text-xs"
    >
      {(["alert", "block"] as const).map((opt) => (
        <button
          key={opt}
          type="button"
          role="tab"
          aria-selected={value === opt}
          onClick={() => onChange(opt)}
          className={cls(opt)}
        >
          {opt.charAt(0).toUpperCase() + opt.slice(1)}
        </button>
      ))}
    </div>
  );
}

// BaselineChipGroup — read-only chip row used for "always-included"
// values (e.g. the process_guard survival set). Visually distinct from
// the editable ChipEditor so operators don't try to remove them. A
// lock icon makes the immutability obvious.
function BaselineChipGroup({ label, values }: { label: string; values: string[] }) {
  return (
    <div className="mb-2 rounded border border-border bg-border/20 p-2">
      <div className="mb-1 text-[10px] uppercase text-text-muted">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-md bg-border/60 px-2 py-0.5 font-mono text-xs text-text-secondary"
            title="locked — translator always merges this into the deployed allow list"
          >
            <span aria-hidden>🔒</span> {v}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Process Guard editor ──────────────────────────────────────────
function ProcessGuardEditor({
  value,
  onChange,
}: {
  value: PolicyProcessGuard | undefined;
  onChange: (next: PolicyProcessGuard) => void;
}) {
  const pg: PolicyProcessGuard =
    value ?? { enable: false, remediate: false, inline: true, allow: [], deny: [] };

  return (
    <div className="space-y-3">
      <ModeToggle
        label="mode"
        mode={modeOf(pg.enable, pg.remediate)}
        onChange={(m) => onChange({ ...pg, ...patchForMode(m) })}
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <div className="mb-1 text-xs uppercase text-text-muted">allow (glob path patterns)</div>
          <BaselineChipGroup
            label="BlueRock baseline (always included)"
            values={PROCESS_GUARD_BASELINE_ALLOW}
          />
          <ChipEditor
            values={pg.allow}
            onChange={(next) => onChange({ ...pg, allow: next })}
            placeholder="/opt/myapp/**"
          />
        </div>
        <div>
          <div className="mb-1 text-xs uppercase text-text-muted">deny (glob path patterns)</div>
          <ChipEditor
            values={pg.deny}
            onChange={(next) => onChange({ ...pg, deny: next })}
            placeholder="**/curl"
          />
        </div>
      </div>
      <DenyExceptionsEditor
        values={pg.deny_exceptions ?? []}
        onChange={(next) => onChange({ ...pg, deny_exceptions: next })}
      />
    </div>
  );
}

function DenyExceptionsEditor({
  values,
  onChange,
}: {
  values: PolicyTargetProcessSelector[];
  onChange: (next: PolicyTargetProcessSelector[]) => void;
}) {
  return (
    <details className="rounded border border-border bg-surface/40 p-3">
      <summary className="cursor-pointer text-xs uppercase text-text-muted">
        deny exceptions <span className="text-text-secondary">(advanced)</span>
      </summary>
      <div className="mt-2 space-y-2">
        <div className="text-xs text-text-muted">
          Allow specific callers to bypass the deny list. <code>path_pattern</code>{" "}
          is the process file path glob (kernel-set, can't be lied about).{" "}
          <code>comm_list</code> narrows further by the kernel-recorded{" "}
          <code>comm</code> string (15-char cap). Empty comm list = match any
          comm.
        </div>
        {values.map((sel, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 rounded border border-border bg-surface p-2 md:grid-cols-2">
            <input
              className="rounded border border-border bg-surface px-2 py-1 font-mono text-xs"
              placeholder="**/bin/bash"
              value={sel.path_pattern}
              onChange={(e) => {
                const next = [...values];
                next[i] = { ...sel, path_pattern: e.target.value };
                onChange(next);
              }}
            />
            <div className="flex items-start gap-2">
              <ChipEditor
                compact
                values={sel.comm_list ?? []}
                onChange={(c) => {
                  const next = [...values];
                  next[i] = { ...sel, comm_list: c };
                  onChange(next);
                }}
                placeholder="comm (e.g. setup-policy-ro)"
              />
              <button
                type="button"
                onClick={() => onChange(values.filter((_, j) => j !== i))}
                className="text-text-muted hover:text-danger"
                aria-label="Remove exception"
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...values, { path_pattern: "", comm_list: [] }])}
          className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-elev"
        >
          <Plus size={12} /> Add exception
        </button>
      </div>
    </details>
  );
}

// ── Sensitive File Access editor ───────────────────────────────────
// One panel: mode → sensitive paths → exceptions. Exceptions are the
// optional escape hatch (program-scoped paths that bypass the deny);
// previously they lived in their own bordered card which read as a
// separate concern. Now they're a labeled subsection inside the same
// panel so the relationship is visually obvious.
function SensitiveFileAccessEditor({
  value,
  onChange,
}: {
  value: PolicySensitiveFileAccess | undefined;
  onChange: (next: PolicySensitiveFileAccess) => void;
}) {
  const sfa: PolicySensitiveFileAccess =
    value ?? { enable: false, remediate: false, inline: true, paths: [] };
  const paths = sfa.paths;
  const exceptions = sfa.exceptions ?? [];

  return (
    <div className="rounded border border-border bg-surface/40 p-3 space-y-4">
      <ModeToggle
        label="mode"
        mode={modeOf(sfa.enable, sfa.remediate)}
        onChange={(m) => onChange({ ...sfa, ...patchForMode(m) })}
      />

      {/* Sensitive paths — the primary list. */}
      <div className="space-y-1.5">
        <div className="text-xs uppercase text-text-muted">sensitive paths</div>
        {paths.map((e, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className="flex-1 rounded border border-border bg-surface px-2 py-1 font-mono text-xs"
              placeholder="/etc/shadow"
              value={e.path}
              onChange={(ev) => {
                const next = [...paths];
                next[i] = { ...e, path: ev.target.value };
                onChange({ ...sfa, paths: next });
              }}
            />
            <label className="flex items-center gap-1 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={!!e.write_only}
                onChange={(ev) => {
                  const next = [...paths];
                  next[i] = { ...e, write_only: ev.target.checked };
                  onChange({ ...sfa, paths: next });
                }}
              />
              write-only
            </label>
            <button
              type="button"
              onClick={() => onChange({ ...sfa, paths: paths.filter((_, j) => j !== i) })}
              className="text-text-muted hover:text-danger"
              aria-label="Remove path"
              title="Remove"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange({ ...sfa, paths: [...paths, { path: "", write_only: false }] })}
          className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-elev"
        >
          <Plus size={12} /> Add path
        </button>
      </div>

      {/* Exceptions — optional escape hatch, visually de-emphasized but in the same panel. */}
      <div className="space-y-1.5 border-t border-border pt-3">
        <div className="text-xs uppercase text-text-muted">
          exceptions <span className="normal-case text-text-secondary">(optional — program + file globs that bypass the deny)</span>
        </div>
        {exceptions.map((e, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_auto]">
            <input
              className="rounded border border-border bg-surface px-2 py-1 font-mono text-xs"
              placeholder="program (e.g. /usr/sbin/sshd)"
              value={e.program}
              onChange={(ev) => {
                const next = [...exceptions];
                next[i] = { ...e, program: ev.target.value };
                onChange({ ...sfa, exceptions: next });
              }}
            />
            <input
              className="rounded border border-border bg-surface px-2 py-1 font-mono text-xs"
              placeholder="file (e.g. **/*)"
              value={e.file}
              onChange={(ev) => {
                const next = [...exceptions];
                next[i] = { ...e, file: ev.target.value };
                onChange({ ...sfa, exceptions: next });
              }}
            />
            <button
              type="button"
              onClick={() => onChange({ ...sfa, exceptions: exceptions.filter((_, j) => j !== i) })}
              className="text-text-muted hover:text-danger"
              aria-label="Remove exception"
              title="Remove"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange({ ...sfa, exceptions: [...exceptions, { program: "", file: "" }] })}
          className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-elev"
        >
          <Plus size={12} /> Add exception
        </button>
      </div>
    </div>
  );
}

// ── Syscalls editor ───────────────────────────────────────────────
// 7 sub-rule cards, each with toggle triplet + per-rule flags.
function SyscallsEditor({
  value,
  onChange,
}: {
  value: PolicyBraceSyscalls | undefined;
  onChange: (next: PolicyBraceSyscalls) => void;
}) {
  const s: PolicyBraceSyscalls = value ?? { enable: false };
  const patch = (p: Partial<PolicyBraceSyscalls>) => onChange({ ...s, ...p });

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 rounded border border-border bg-surface/40 p-3 text-sm">
        <input type="checkbox" checked={!!s.enable} onChange={(e) => patch({ enable: e.target.checked })} />
        <span>
          <span className="font-medium">Enable syscall observers</span>
          <span className="ml-1 text-xs text-text-muted">— umbrella for the per-syscall rules below</span>
        </span>
      </label>

      <SyscallCard title="exec" desc="execve / execveat. Observation only — for exec policy use Process Guard.">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!s.exec?.enable}
            onChange={(e) => patch({ exec: { ...(s.exec ?? { enable: false, track_clone: false }), enable: e.target.checked } })}
          />
          Enable
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!s.exec?.track_clone}
            onChange={(e) =>
              patch({ exec: { ...(s.exec ?? { enable: false }), track_clone: e.target.checked } })
            }
          />
          Track clone/clone3 (full process tree; extra overhead)
        </label>
      </SyscallCard>

      <SyscallCard title="open" desc="open / openat. Observation only.">
        <ModeToggle
          mode={modeOf(s.open?.enable, s.open?.remediate)}
          allowBlock={false}
          onChange={(m) =>
            patch({ open: { ...(s.open ?? {}), ...patchForMode(m) } as PolicyBraceSyscalls["open"] })
          }
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={s.open?.allow_read_only !== false}
            onChange={(e) =>
              patch({ open: { ...(s.open ?? {}), allow_read_only: e.target.checked } as PolicyBraceSyscalls["open"] })
            }
          />
          Include read-only opens (uncheck to fire only on writes)
        </label>
      </SyscallCard>

      <SyscallCard title="socket" desc="socket(2). Observation only — for network restriction use brace.network.firewall.">
        <ModeToggle
          mode={modeOf(s.socket?.enable, s.socket?.remediate)}
          allowBlock={false}
          onChange={(m) =>
            patch({ socket: { ...(s.socket ?? {}), ...patchForMode(m) } as PolicyBraceSyscalls["socket"] })
          }
        />
      </SyscallCard>

      <SyscallCard title="connect" desc="connect(2). Observation only.">
        <ModeToggle
          mode={modeOf(s.connect?.enable, s.connect?.remediate)}
          allowBlock={false}
          onChange={(m) =>
            patch({ connect: { ...(s.connect ?? {}), ...patchForMode(m) } as PolicyBraceSyscalls["connect"] })
          }
        />
      </SyscallCard>

      <SyscallCard title="delete" desc="unlink / unlinkat / rmdir. Observation only.">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!s.delete?.enable}
            onChange={(e) =>
              patch({ delete: { ...(s.delete ?? { enable: false }), enable: e.target.checked } })
            }
          />
          Enable
        </label>
      </SyscallCard>

      <SyscallCard
        title="mmap_exec"
        desc="mmap+PROT_EXEC. Detects fileless exec (memfd), suspicious .so loads. Can block."
      >
        <ModeToggle
          mode={modeOf(s.mmap_exec?.enable, s.mmap_exec?.remediate)}
          onChange={(m) =>
            patch({ mmap_exec: { ...(s.mmap_exec ?? {}), ...patchForMode(m) } as PolicyBraceSyscalls["mmap_exec"] })
          }
        />
      </SyscallCard>

      <SyscallCard
        title="suspicious_syscalls"
        desc="unshare(2) (namespace escape) + ptrace(2) (process injection). Can block."
      >
        <ModeToggle
          mode={modeOf(s.suspicious_syscalls?.enable, s.suspicious_syscalls?.remediate)}
          onChange={(m) =>
            patch({
              suspicious_syscalls: {
                ...(s.suspicious_syscalls ?? {}),
                ...patchForMode(m),
              } as PolicyBraceSyscalls["suspicious_syscalls"],
            })
          }
        />
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!s.suspicious_syscalls?.allow_unshare}
              onChange={(e) =>
                patch({
                  suspicious_syscalls: {
                    ...(s.suspicious_syscalls ?? {}),
                    allow_unshare: e.target.checked,
                  } as PolicyBraceSyscalls["suspicious_syscalls"],
                })
              }
            />
            allow unshare (namespace tooling)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!s.suspicious_syscalls?.allow_ptrace}
              onChange={(e) =>
                patch({
                  suspicious_syscalls: {
                    ...(s.suspicious_syscalls ?? {}),
                    allow_ptrace: e.target.checked,
                  } as PolicyBraceSyscalls["suspicious_syscalls"],
                })
              }
            />
            allow ptrace (debuggers, strace)
          </label>
        </div>
      </SyscallCard>
    </div>
  );
}

function SyscallCard({
  title,
  desc,
  children,
}: {
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2 rounded border border-border bg-surface/30 p-3">
      <div>
        <div className="font-mono text-sm font-medium">{title}</div>
        <div className="text-xs text-text-muted">{desc}</div>
      </div>
      {children}
    </div>
  );
}
